/**
 * The one number of the screen: "412 documents moved in the last hour." with, under it in neutral text, what
 * failed ("● 19 documents failed in the last hour", the dot the only red), and the last 24 hours as small
 * bars (failed ones in red) labelled "last 24 h" right under them. Incidents are the feed's job; the tenant
 * and the clock live in the feed header. An empty vault says "Waiting for the first document."
 */
import { Num } from '../design/primitives.tsx';
import { count } from '../lib/format.ts';
import type { Bar } from './model.ts';
import { historyLine, noDataHint, type History } from './emptyState.ts';

/** Documents per hour over the last day; the current hour, still filling, is the dark bar. */
function Spark({ bars }: { bars: Bar[] }) {
  const max = Math.max(1, ...bars.map((b) => b.count));
  const h = (n: number) => `${Math.max(n > 0 ? 2 : 1, Math.round((n / max) * 36))}px`;
  const total = bars.reduce((n, b) => n + b.count, 0);
  const failed = bars.reduce((n, b) => n + b.failed, 0);
  return (
    <div className="now-spark-wrap">
      <div className="now-spark" role="img" aria-label={`${count(total)} documents in 24 hours, ${count(failed)} failed`}>
        {bars.map((b) => (
          <i key={b.time} style={{ height: h(b.count) }}>{b.failed > 0 && <b style={{ height: h(b.failed) }} />}</i>
        ))}
      </div>
      <span className="now-spark-lbl" aria-hidden="true">last 24 h</span>
    </div>
  );
}

export function Sentence({ moved, failed, bars, empty, loading, history, now, envName, onLook }: {
  moved: number | null; failed: number | null; bars: Bar[]; empty: boolean; loading: boolean;
  /** Empty only: what the environment holds from before (null until known). */
  history?: History | null; now: number; envName?: string | null; onLook?: () => void;
}) {
  const older = empty && !!history && history.total > 0;
  if (loading) {
    return (
      <div aria-busy="true">
        <div className="now-head">
          <div className="now-sentence">
            <span className="mv-shimmer rounded-chip inline-block h-[50px] w-[120px] self-center" />
            <span className="mv-shimmer rounded-chip inline-block h-[20px] w-[420px] max-w-full self-center" />
          </div>
          <div className="now-spark" aria-hidden="true">
            {Array.from({ length: 24 }, (_, i) => <i key={i} className="mv-shimmer" style={{ height: 8 + ((i * 7) % 20) }} />)}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="now-head">
      <div className="min-w-0">
        <h1 className="now-sentence m-0 font-normal">
          {empty ? (
            <span className="text-title font-medium text-n9">{older ? 'Nothing moved in the last 24 hours.' : 'Waiting for the first document.'}</span>
          ) : (
            <>
              <Num size="display" value={moved} />
              <span className="text-title font-medium text-n9">documents moved in the last hour.</span>
            </>
          )}
        </h1>
        {empty && history && (
          older ? (
            <>
              <p className="m-0 mt-1 text-body text-n8">{historyLine(history, now)}</p>
              <button type="button" onClick={onLook} className="mt-3 p-0 bg-transparent border-0 cursor-pointer text-body font-medium text-accent hover:underline">Look at the data</button>
            </>
          ) : (
            <p className="m-0 mt-1 text-body text-n8">{noDataHint(envName)}</p>
          )
        )}
        {!empty && (
          failed ? (
            <p className="m-0 mt-1 text-body text-n9 inline-flex items-center gap-2">
              <i className="size-2 rounded-full bg-fail shrink-0" aria-hidden="true" />
              {count(failed)} {failed === 1 ? 'document' : 'documents'} failed in the last hour
            </p>
          ) : (
            <p className="m-0 mt-1 text-body text-n8">None failed in the last hour</p>
          )
        )}
      </div>
      {!empty && bars.length > 0 && <Spark bars={bars} />}
    </div>
  );
}
